import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
  ExclamationCircleFilled,
  MinusCircleOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { extractApiErrorMessage } from '@/api/client';
import { listCommunityCourses } from '@/api/courses';
import { listAssignmentDisciplineReports } from '@/api/discipline';
import { listCommunities } from '@/api/students';
import { listCommunityTeams } from '@/api/teams';
import {
  ASSIGNMENT_TYPE_LABELS,
  SUBMISSION_STATUS_LABELS,
  type AssignmentType,
} from '@/types/assignments';
import type {
  AssignmentDisciplineSummary,
  AttendanceDate,
  MemberAttendance,
} from '@/types/discipline';

const { Title, Text } = Typography;

const TYPE_OPTIONS = (Object.keys(ASSIGNMENT_TYPE_LABELS) as AssignmentType[])
  .map((value) => ({ value, label: ASSIGNMENT_TYPE_LABELS[value] }));

/* ----------------------------------------------------------------- */
/* URL-driven filters                                                 */
/* ----------------------------------------------------------------- */

interface ParsedFilters {
  communityId: number | null;
  courseId: number | null;
  teamIds: number[];
  /** True when the URL has `allTeams=1`. Sends no `teamId` param to the API,
   *  which makes the backend fall back to {@code findByCourseId} on Team —
   *  course-scoped teams only, much smaller set than the whole community. */
  allTeams: boolean;
  type?: AssignmentType;
}

function parseCsvNumbers(raw: string | null): number[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function parseFilters(params: URLSearchParams): ParsedFilters {
  const cid = params.get('communityId');
  const courseRaw = params.get('courseId');
  const typeRaw = params.get('type') as AssignmentType | null;
  return {
    communityId: cid && Number(cid) > 0 ? Number(cid) : null,
    courseId: courseRaw && Number(courseRaw) > 0 ? Number(courseRaw) : null,
    teamIds: parseCsvNumbers(params.get('teamIds')),
    allTeams: params.get('allTeams') === '1',
    type:
      typeRaw && typeRaw in ASSIGNMENT_TYPE_LABELS ? typeRaw : undefined,
  };
}

/* ----------------------------------------------------------------- */
/* Status classification                                              */
/* ----------------------------------------------------------------- */

type CellState = 'on-time' | 'late' | 'missing' | 'pending';

/**
 * Map one (submission, dueDate) pair to a display state.
 *
 * <p>"pending" = bài chưa đến hạn AND học viên chưa nộp. We don't flag this as
 * a violation because the student still has time to submit — only assignments
 * whose due day has already passed should count as "Chưa nộp".
 *
 * <p>Comparison is day-granularity ({@code isBefore(today, 'day')}): an
 * assignment due 23:59 today is NOT yet missing — it becomes missing at
 * midnight when the date rolls over.
 */
function classify(
  attendance: AttendanceDate | null,
  dueDate: string | null,
): CellState {
  if (attendance && attendance.date) {
    if (!dueDate) return 'on-time';
    return dayjs(attendance.date).isAfter(dayjs(dueDate)) ? 'late' : 'on-time';
  }
  // Not submitted yet.
  if (!dueDate) return 'pending';
  return dayjs(dueDate).isBefore(dayjs(), 'day') ? 'missing' : 'pending';
}

function cellStateToCsv(state: CellState): string {
  switch (state) {
    case 'on-time':
      return 'Đã nộp';
    case 'late':
      return 'Nộp muộn';
    case 'missing':
      return 'Chưa nộp';
    case 'pending':
      return 'Chưa đến hạn';
  }
}

function StatusCell({
  attendance,
  dueDate,
  assignmentTitle,
}: {
  attendance: AttendanceDate | null;
  dueDate: string | null;
  assignmentTitle: string | null;
}) {
  const state = classify(attendance, dueDate);
  let icon;
  let lines: string[];
  if (state === 'on-time') {
    icon = <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />;
    lines = [
      `Bài: ${assignmentTitle ?? '—'}`,
      `Đã nộp${attendance?.date ? ` lúc ${formatDateTime(attendance.date)}` : ''}`,
      attendance?.status
        ? `Trạng thái: ${SUBMISSION_STATUS_LABELS[attendance.status]}`
        : '',
    ].filter(Boolean);
  } else if (state === 'late') {
    icon = <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 18 }} />;
    lines = [
      `Bài: ${assignmentTitle ?? '—'}`,
      `Nộp muộn${attendance?.date ? ` lúc ${formatDateTime(attendance.date)}` : ''}`,
      dueDate ? `Hạn: ${formatDateTime(dueDate)}` : '',
    ].filter(Boolean);
  } else if (state === 'pending') {
    // Bài chưa đến hạn, học viên chưa nộp — neutral state, không tính lỗi.
    icon = <MinusCircleOutlined style={{ color: 'rgba(0,0,0,0.35)', fontSize: 18 }} />;
    lines = [
      `Bài: ${assignmentTitle ?? '—'}`,
      'Chưa đến hạn',
      dueDate ? `Hạn: ${formatDateTime(dueDate)}` : '',
    ].filter(Boolean);
  } else {
    icon = <CloseCircleFilled style={{ color: '#f5222d', fontSize: 18 }} />;
    lines = [
      `Bài: ${assignmentTitle ?? '—'}`,
      'Chưa nộp',
      dueDate ? `Hạn: ${formatDateTime(dueDate)}` : '',
    ].filter(Boolean);
  }
  return (
    <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</div>}>
      <span style={{ display: 'inline-flex', cursor: 'help' }}>{icon}</span>
    </Tooltip>
  );
}

/* ----------------------------------------------------------------- */
/* Helpers                                                            */
/* ----------------------------------------------------------------- */

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
}

function formatShortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM') : '—';
}

function latestAttendance(
  attendances: readonly AttendanceDate[],
): AttendanceDate | null {
  let latest: AttendanceDate | null = null;
  for (const a of attendances) {
    if (!a.date) continue;
    if (!latest || dayjs(a.date).isAfter(dayjs(latest.date))) {
      latest = a;
    }
  }
  return latest;
}

/* ----------------------------------------------------------------- */
/* Pivot: response is per (assignment × team) row. Matrix needs       */
/* per-member rows × per-assignment columns. Dedupe by id.            */
/* ----------------------------------------------------------------- */

interface UniqueAssignment {
  assignmentId: number;
  assignmentTitle: string | null;
  dueDate: string | null;
}

interface MemberRow {
  member: MemberAttendance;
  /** key = assignmentId, value = latest attendance for that assignment (or null). */
  byAssignment: Record<number, AttendanceDate | null>;
}

interface PivotResult {
  assignments: UniqueAssignment[]; // chronological ascending
  members: MemberRow[]; // sorted: missing-most first, then alphabetical
}

function pivot(reports: readonly AssignmentDisciplineSummary[]): PivotResult {
  const assignmentMap = new Map<number, UniqueAssignment>();
  const memberMap = new Map<number, MemberRow>();

  for (const r of reports) {
    if (!assignmentMap.has(r.assignmentId)) {
      assignmentMap.set(r.assignmentId, {
        assignmentId: r.assignmentId,
        assignmentTitle: r.assignmentTitle,
        dueDate: r.dueDate,
      });
    }
    for (const m of r.memberAttendances) {
      let row = memberMap.get(m.userId);
      if (!row) {
        row = { member: m, byAssignment: {} };
        memberMap.set(m.userId, row);
      }
      const latest = latestAttendance(m.attendanceDates);
      // If multiple team rows have submissions for the same (member, assignment),
      // keep the latest of all of them (shouldn't happen in practice but safe).
      const existing = row.byAssignment[r.assignmentId];
      if (!existing) {
        row.byAssignment[r.assignmentId] = latest;
      } else if (
        latest &&
        latest.date &&
        (!existing.date || dayjs(latest.date).isAfter(dayjs(existing.date)))
      ) {
        row.byAssignment[r.assignmentId] = latest;
      }
    }
  }

  const assignments = Array.from(assignmentMap.values()).sort(
    (a, b) =>
      (a.dueDate ? dayjs(a.dueDate).valueOf() : 0) -
      (b.dueDate ? dayjs(b.dueDate).valueOf() : 0),
  );

  const members = Array.from(memberMap.values()).sort((a, b) => {
    const aMissing = countByState(a, assignments).missing;
    const bMissing = countByState(b, assignments).missing;
    if (aMissing !== bMissing) return bMissing - aMissing;
    return (a.member.userFullName ?? '').localeCompare(
      b.member.userFullName ?? '',
      'vi',
    );
  });

  return { assignments, members };
}

interface StateCounts {
  onTime: number;
  late: number;
  missing: number;
}

/**
 * Single-pass tally of submission states for one student across the assignment
 * set. Used both by the pivot's default sort (members with the most "missing"
 * to the top) and by the two summary columns next to the member name.
 */
function countByState(
  row: MemberRow,
  assignments: readonly UniqueAssignment[],
): StateCounts {
  let onTime = 0;
  let late = 0;
  let missing = 0;
  for (const a of assignments) {
    const state = classify(row.byAssignment[a.assignmentId] ?? null, a.dueDate);
    if (state === 'on-time') onTime++;
    else if (state === 'late') late++;
    else if (state === 'missing') missing++;
    // 'pending' (chưa đến hạn) intentionally not counted — học viên vẫn còn
    // thời gian để nộp, không tính là vi phạm.
  }
  return { onTime, late, missing };
}

/* ----------------------------------------------------------------- */
/* CSV export                                                         */
/* ----------------------------------------------------------------- */

function exportCsv(
  communityName: string | null,
  courseName: string | null,
  pivoted: PivotResult,
): void {
  const header = [
    'Họ và tên',
    'Email',
    'SĐT',
    ...pivoted.assignments.map(
      (a) => `${formatShortDate(a.dueDate)} - ${a.assignmentTitle ?? `#${a.assignmentId}`}`,
    ),
  ];

  const dataLines = pivoted.members.map((row) => [
    row.member.userFullName ?? '',
    row.member.userEmail ?? '',
    row.member.userMobile ?? '',
    ...pivoted.assignments.map((a) =>
      cellStateToCsv(classify(row.byAssignment[a.assignmentId] ?? null, a.dueDate)),
    ),
  ]);

  const csv = [header, ...dataLines]
    .map((cells) => cells.map(escapeCsvCell).join(','))
    .join('\n');

  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const stamp = dayjs().format('YYYYMMDD-HHmm');
  const safe = (s: string | null) =>
    (s ?? '').replace(/[^A-Za-z0-9-_]+/g, '-').replace(/^-+|-+$/g, '') || 'discipline';
  link.href = url;
  link.download = `ki-luat-${safe(communityName)}-${safe(courseName)}-${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/* ----------------------------------------------------------------- */
/* Page                                                                */
/* ----------------------------------------------------------------- */

export function AssignmentDisciplinePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const communitiesQuery = useQuery({
    queryKey: ['admin-communities'],
    queryFn: listCommunities,
    staleTime: 5 * 60_000,
  });

  const coursesQuery = useQuery({
    queryKey: ['discipline-courses', filters.communityId],
    queryFn: () => listCommunityCourses(filters.communityId as number),
    enabled: filters.communityId !== null,
    staleTime: 60_000,
  });

  const teamsQuery = useQuery({
    queryKey: ['discipline-teams', filters.communityId],
    queryFn: () => listCommunityTeams(filters.communityId as number),
    enabled: filters.communityId !== null,
    staleTime: 60_000,
  });

  // Auto-fill courseId + teamIds when their dropdown data loads, so the
  // matrix shows up immediately without the user having to click anything.
  // Also clears stale IDs that don't match the current community (after a
  // sidebar switch). User can still narrow the team selection manually.
  useEffect(() => {
    const courses = coursesQuery.data;
    const teams = teamsQuery.data;
    if (!courses || !teams) return;

    const next = new URLSearchParams(searchParams);
    let changed = false;

    // Course: keep current if still valid, else pick the first (newest).
    const currentCourseValid =
      filters.courseId !== null &&
      courses.some((c) => c.id === filters.courseId);
    if (!currentCourseValid) {
      const first = courses[0];
      if (first) {
        next.set('courseId', String(first.id));
      } else {
        next.delete('courseId');
      }
      changed = true;
    }

    // Skip team auto-fill when `allTeams=1` — the user has explicitly opted
    // into the no-teamId mode (backend uses course-scoped findByCourseId).
    if (!filters.allTeams) {
      // Teams: keep ones still in this community; if NONE left, default to the
      // FIRST team only. (Picking many teams fans the underlying private report
      // API out to teams × assignments — 10+ seconds. The "Tất cả" button
      // uses the dedicated no-teamId path instead, which is course-scoped
      // and fast.) User can multi-select more teams after page load.
      const validTeamIds = filters.teamIds.filter((id) =>
        teams.some((t) => t.id === id),
      );
      if (validTeamIds.length !== filters.teamIds.length) {
        changed = true;
      }
      if (validTeamIds.length === 0) {
        const firstTeam = teams[0];
        if (firstTeam) {
          next.set('teamIds', String(firstTeam.id));
          changed = true;
        } else {
          next.delete('teamIds');
        }
      } else if (validTeamIds.length !== filters.teamIds.length) {
        next.set('teamIds', validTeamIds.join(','));
      }
    }

    // Default to NORMAL assignments (the day-to-day discipline tracker view).
    // Excludes WEEK summaries and EXAM rows — usually not what "kỉ luật" cares
    // about. User can clear the filter to see everything.
    if (filters.type === undefined && !searchParams.has('type')) {
      next.set('type', 'NORMAL');
      changed = true;
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coursesQuery.data, teamsQuery.data, filters.communityId]);

  const communityName = useMemo(() => {
    if (filters.communityId === null) return null;
    const c = (communitiesQuery.data ?? []).find(
      (x) => x.id === filters.communityId,
    );
    return c?.name ?? null;
  }, [communitiesQuery.data, filters.communityId]);

  const courseName = useMemo(() => {
    if (filters.courseId === null) return null;
    const c = (coursesQuery.data ?? []).find((x) => x.id === filters.courseId);
    return c?.name ?? null;
  }, [coursesQuery.data, filters.courseId]);

  const canQuery =
    filters.communityId !== null &&
    filters.courseId !== null &&
    (filters.allTeams || filters.teamIds.length > 0);

  const reportQuery = useQuery({
    queryKey: [
      'discipline-assignments',
      filters.courseId,
      filters.allTeams ? 'all' : filters.teamIds,
      filters.type,
    ],
    queryFn: () =>
      listAssignmentDisciplineReports({
        courseId: filters.courseId as number,
        // `allTeams=1` ⇒ send no teamId param. Backend falls back to
        // `findByCourseId` on Team — course-scoped, much faster than passing
        // every team id explicitly (which fans out to teams × assignments).
        teamIds: filters.allTeams ? undefined : filters.teamIds,
        type: filters.type,
      }),
    enabled: canQuery,
  });

  const pivoted = useMemo(
    () => pivot(reportQuery.data ?? []),
    [reportQuery.data],
  );

  const updateParam = (
    patch: Partial<Record<string, string | undefined>>,
  ) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '' || v === null) next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  const error = reportQuery.error
    ? extractApiErrorMessage(reportQuery.error, 'Không tải được báo cáo kỉ luật.')
    : null;

  const courseOptions = (coursesQuery.data ?? []).map((c) => ({
    value: c.id,
    label: c.name ?? `Khoá #${c.id}`,
  }));
  const teamOptions = (teamsQuery.data ?? []).map((t) => ({
    value: t.id,
    label: t.name ?? `Nhóm #${t.id}`,
  }));

  const columns: ColumnsType<MemberRow> = [
    {
      title: 'Thành viên',
      key: 'member',
      fixed: 'left',
      width: 240,
      render: (_, row) => (
        <Space>
          <Avatar
            src={row.member.userAvatarUrl ?? undefined}
            icon={<UserOutlined />}
            size={32}
          />
          <Space direction="vertical" size={0}>
            <Text strong>{row.member.userFullName ?? '—'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.member.userEmail ?? row.member.userMobile ?? `#${row.member.userId}`}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      // Summary column: count of "missing" submissions per student. Pinned left
      // alongside the member column so it stays visible while the user scrolls
      // through dozens of assignment columns to the right.
      title: (
        <Tooltip title="Chưa nộp">
          <CloseCircleFilled style={{ color: '#f5222d', fontSize: 16 }} />
        </Tooltip>
      ),
      key: 'count-missing',
      fixed: 'left',
      width: 64,
      align: 'center',
      render: (_, row) => {
        const { missing } = countByState(row, pivoted.assignments);
        return (
          <Text
            strong={missing > 0}
            style={{ color: missing > 0 ? '#f5222d' : 'rgba(0,0,0,0.35)' }}
          >
            {missing}
          </Text>
        );
      },
    },
    {
      // Summary column: count of "late" submissions per student. Same pinning
      // logic as the missing column above.
      title: (
        <Tooltip title="Nộp muộn">
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 16 }} />
        </Tooltip>
      ),
      key: 'count-late',
      fixed: 'left',
      width: 64,
      align: 'center',
      render: (_, row) => {
        const { late } = countByState(row, pivoted.assignments);
        return (
          <Text
            strong={late > 0}
            style={{ color: late > 0 ? '#faad14' : 'rgba(0,0,0,0.35)' }}
          >
            {late}
          </Text>
        );
      },
    },
    ...pivoted.assignments.map((a) => ({
      title: (
        <Tooltip title={a.assignmentTitle ?? `Assignment #${a.assignmentId}`}>
          <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontWeight: 600 }}>{formatShortDate(a.dueDate)}</div>
            <div
              style={{
                fontSize: 11,
                color: 'rgba(0,0,0,0.55)',
                fontWeight: 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 80,
              }}
            >
              {a.assignmentTitle ?? `#${a.assignmentId}`}
            </div>
          </div>
        </Tooltip>
      ),
      key: `a-${a.assignmentId}`,
      width: 92,
      align: 'center' as const,
      render: (_: unknown, row: MemberRow) => (
        <StatusCell
          attendance={row.byAssignment[a.assignmentId] ?? null}
          dueDate={a.dueDate}
          assignmentTitle={a.assignmentTitle}
        />
      ),
    })),
  ];

  return (
    <div className="discipline-list">
      <div className="student-list-header">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Kỉ Luật · Bài tập
          </Title>
          <Space size={6} wrap>
            <Text type="secondary">
              {filters.communityId === null
                ? 'Chọn một cộng đồng ở menu bên trái để xem báo cáo'
                : communityName ?? `Community #${filters.communityId}`}
            </Text>
            {pivoted.assignments.length > 0 ? (
              <Tag color="geekblue">
                {pivoted.members.length} học viên ·{' '}
                {pivoted.assignments.length} bài
              </Tag>
            ) : null}
            {filters.allTeams ? (
              <Tag color="purple">Tất cả team trong khoá</Tag>
            ) : null}
          </Space>
        </div>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            type="primary"
            onClick={() => exportCsv(communityName, courseName, pivoted)}
            disabled={pivoted.assignments.length === 0}
          >
            Xuất CSV
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => reportQuery.refetch()}
            loading={reportQuery.isFetching}
            disabled={!canQuery}
          >
            Tải lại
          </Button>
        </Space>
      </div>

      <Card variant="borderless" className="filter-card">
        <Space wrap size="middle" style={{ width: '100%' }}>
          <Select<number>
            allowClear
            showSearch
            placeholder="Chọn khoá học"
            options={courseOptions}
            value={filters.courseId ?? undefined}
            onChange={(value) =>
              updateParam({
                courseId: value === undefined || value === null ? undefined : String(value),
              })
            }
            loading={coursesQuery.isLoading}
            optionFilterProp="label"
            disabled={filters.communityId === null}
            style={{ minWidth: 240, maxWidth: 360 }}
          />

          <Select<number[]>
            mode="multiple"
            allowClear
            showSearch
            placeholder={filters.allTeams ? 'Tất cả team (course-scoped)' : 'Chọn nhóm'}
            options={teamOptions}
            value={filters.allTeams ? [] : filters.teamIds}
            onChange={(values) =>
              // Picking specific teams cancels `allTeams` mode.
              updateParam({
                teamIds: values.length === 0 ? undefined : values.join(','),
                allTeams: undefined,
              })
            }
            loading={teamsQuery.isLoading}
            optionFilterProp="label"
            maxTagCount="responsive"
            disabled={filters.communityId === null || filters.allTeams}
            style={{ minWidth: 220, maxWidth: 360 }}
          />

          <Tooltip
            title={
              filters.allTeams
                ? 'Tắt chế độ Tất cả — quay lại lọc theo team (mặc định team đầu).'
                : 'Lấy tất cả thành viên trong khoá. Backend dùng course-scoped query, nhanh hơn nhiều.'
            }
          >
            <Button
              type={filters.allTeams ? 'primary' : 'default'}
              onClick={() => {
                if (filters.allTeams) {
                  // Toggle OFF: restore the first team so the matrix doesn't
                  // render empty. User can pick more teams from the dropdown
                  // afterwards.
                  const firstTeam = (teamsQuery.data ?? [])[0];
                  updateParam({
                    allTeams: undefined,
                    teamIds: firstTeam ? String(firstTeam.id) : undefined,
                  });
                } else {
                  updateParam({ allTeams: '1', teamIds: undefined });
                }
              }}
              disabled={
                filters.communityId === null || filters.courseId === null
              }
            >
              {filters.allTeams ? 'Tất cả (đang bật)' : 'Tất cả'}
            </Button>
          </Tooltip>

          <Select
            allowClear
            placeholder="Loại bài (Tất cả)"
            options={TYPE_OPTIONS}
            value={filters.type}
            onChange={(value) => updateParam({ type: value })}
            style={{ width: 200 }}
          />
        </Space>
      </Card>

      {filters.communityId === null ? (
        <Card variant="borderless">
          <Empty description="Chưa chọn cộng đồng" />
        </Card>
      ) : !canQuery ? (
        <Card variant="borderless">
          <Empty
            description={
              filters.courseId === null
                ? 'Hãy chọn khoá học để bắt đầu'
                : 'Hãy chọn ít nhất 1 nhóm để bắt đầu'
            }
          />
        </Card>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="Lỗi"
          description={error}
          style={{ marginTop: 16 }}
        />
      ) : (
        <Card variant="borderless" className="table-card">
          <Table<MemberRow>
            rowKey={(r) => r.member.userId}
            columns={columns}
            dataSource={pivoted.members}
            loading={reportQuery.isFetching}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content', y: 600 }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    pivoted.assignments.length === 0
                      ? 'Không có bài tập nào trong phạm vi lọc'
                      : 'Không có học viên nào trong các nhóm đã chọn'
                  }
                />
              ),
            }}
          />
        </Card>
      )}
    </div>
  );
}
